import React, { useState } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { useDatos } from '../../contexts/DatosContext';
import type { Sucursal } from '../../types';
const claseInput = 'h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-2xs font-semibold uppercase tracking-wide text-gris-500';
export function EditorSucursal({
  sucursal,
  esNueva,
  clavesUsadas,
  onGuardar,
  onCancelar
}: {
  sucursal: Sucursal;
  esNueva: boolean;
  clavesUsadas: string[];
  onGuardar: (sucursal: Sucursal) => void;
  onCancelar: () => void;
}) {
  const {
    abogados
  } = useDatos();
  const [borrador, setBorrador] = useState<Sucursal>(sucursal);
  const [error, setError] = useState('');
  const cambiar = <C extends keyof Sucursal,>(campo: C, valor: Sucursal[C]) => setBorrador(prev => ({
    ...prev,
    [campo]: valor
  }));
  const guardar = () => {
    const clave = borrador.clave.trim().toUpperCase();
    if (!borrador.nombre.trim()) {
      setError('El nombre de la sucursal es obligatorio.');
      return;
    }
    if (!/^[A-ZÑ]{2,4}$/.test(clave)) {
      setError('La clave debe tener entre 2 y 4 letras; se usará en el localizador.');
      return;
    }
    if (clavesUsadas.includes(clave)) {
      setError(`La clave ${clave} ya está en uso por otra sucursal.`);
      return;
    }
    if (!borrador.ciudad.trim() || !borrador.estado.trim()) {
      setError('Captura ciudad y estado.');
      return;
    }
    onGuardar({
      ...borrador,
      clave,
      nombre: borrador.nombre.trim(),
      direccion: borrador.direccion.trim(),
      ciudad: borrador.ciudad.trim(),
      estado: borrador.estado.trim(),
      telefono: borrador.telefono.trim()
    });
  };
  return <div className="border-b border-marino-200 bg-marino-50 px-5 py-5" data-id="element-86" data-mp="2wszx21s46kmr">
      <h2 className="text-sm font-semibold text-marino-900" data-id="element-87" data-mp="2wr6t4u8vyeig">
        {esNueva ? 'Nueva sucursal' : `Editar «${sucursal.nombre}»`}
      </h2>

      <div className="mt-4 grid gap-4 md:grid-cols-3" data-id="element-88" data-mp="2yxmuasci7t3t">
        <label className="md:col-span-2" data-id="element-89" data-mp="2ywv6i3p5ezo3">
          <span className={claseEtiqueta} data-id="element-90" data-mp="33vonw77k3atj">Nombre *</span>
          <input autoFocus className={`mt-1.5 ${claseInput}`} value={borrador.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Ej. Puebla" data-id="element-91" data-mp="33yavg6ug5qbr" />
        </label>
        <label data-id="element-92" data-mp="2g7ev2lbxdmsw">
          <span className={claseEtiqueta} data-id="element-93" data-mp="2g7oqiofqom5z">Clave (2-4 letras) *</span>
          <input className={`mt-1.5 ${claseInput} font-mono uppercase`} maxLength={4} value={borrador.clave} onChange={e => cambiar('clave', e.target.value.toUpperCase())} placeholder="PUE" data-id="element-94" data-mp="2l5gnhwv8qyh1" />
          <span className="mt-1 block text-2xs text-gris-400" data-id="element-95" data-mp="2uxmi7ecvntzx">
            Prefijo del localizador: {(borrador.clave || 'XXX').toUpperCase()}-PEN-
            {new Date().getFullYear()}-0001
          </span>
        </label>

        <label className="md:col-span-3" data-id="element-96" data-mp="jk4c0bio7mlm">
          <span className={claseEtiqueta} data-id="element-97" data-mp="jit728dho8sx">Dirección completa</span>
          <input className={`mt-1.5 ${claseInput}`} value={borrador.direccion} onChange={e => cambiar('direccion', e.target.value)} placeholder="Calle, número, colonia y código postal" data-id="element-98" data-mp="fhzidcs82pyz" />
        </label>

        <label data-id="element-99" data-mp="5mfmurhkltar">
          <span className={claseEtiqueta} data-id="element-100" data-mp="l8eorbims3k">Ciudad *</span>
          <input className={`mt-1.5 ${claseInput}`} value={borrador.ciudad} onChange={e => cambiar('ciudad', e.target.value)} data-id="element-101" data-mp="mtaek9kgh30" />
        </label>
        <label data-id="element-102" data-mp="3nz41vp86d563">
          <span className={claseEtiqueta} data-id="element-103" data-mp="3nykjk7em4erj">Estado *</span>
          <input className={`mt-1.5 ${claseInput}`} value={borrador.estado} onChange={e => cambiar('estado', e.target.value)} data-id="element-104" data-mp="3izrdv15khs1x" />
        </label>
        <label data-id="element-105" data-mp="390jrxpkrop37">
          <span className={claseEtiqueta} data-id="element-106" data-mp="390s7xqc6saim">Teléfono</span>
          <input className={`tabular mt-1.5 ${claseInput}`} value={borrador.telefono} onChange={e => cambiar('telefono', e.target.value)} placeholder="222 123 4567" data-id="element-107" data-mp="393edxljfihyq" />
        </label>

        <label className="md:col-span-2" data-id="element-108" data-mp="1372jussyqzsb">
          <span className={claseEtiqueta} data-id="element-109" data-mp="13616ghoh617f">Responsable</span>
          <select className={`mt-1.5 ${claseInput}`} value={borrador.responsableId} onChange={e => cambiar('responsableId', e.target.value)} data-id="element-110" data-mp="1865fxnqytvo5">
            <option value="" data-id="element-111" data-mp="z6sbxalm1zat">Sin asignar</option>
            {abogados.filter(a => a.activo).map(a => <option key={a.id} value={a.id} data-id="element-112" data-mp="kbcgp29anxcu">
                  {a.nombre} — cédula {a.cedula}
                </option>)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="mt-3 text-xs font-medium text-peligro-600" data-id="element-113" data-mp="2el6apq4987t5">
          {error}
        </p>}

      <div className="mt-4 flex justify-end gap-2" data-id="element-114" data-mp="7z0a8qwsy72u">
        <button type="button" onClick={onCancelar} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-115" data-mp="7zs7s79ymevr">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-116" data-mp="cxtoiljwy242" />
          Cancelar
        </button>
        <button type="button" onClick={guardar} className="inline-flex h-9 items-center gap-1.5 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-117" data-mp="qoyr9wikvaim">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-118" data-mp="10mwecgopb19n" />
          {esNueva ? 'Agregar sucursal' : 'Guardar cambios'}
        </button>
      </div>
    </div>;
}